<template>
  <div class="w-full h-64 perspective group" @click="isFlipped = !isFlipped">
    <div
      class="relative w-full h-full flip-card-inner transition-transform duration-500"
      :class="{ 'rotate-y-180': isFlipped, 'group-hover:rotate-y-180': !isFlipped }"
    >
      <!-- Front (Food Image and Price) -->
      <div
        class="absolute w-full h-full backface-hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
      >
        <img
          :src="food.image"
          :alt="food.name"
          class="w-full h-full object-cover"
        />
        <div class="absolute top-4 left-4">
          <UBadge color="main" size="lg"> {{ food.price }}€ </UBadge>
        </div>
        <div class="absolute bottom-4 right-4 flex gap-2">
          <UButton color="main" variant="solid" size="sm"> Détails </UButton>
        </div>
      </div>

      <!-- Back (Food Details) -->
      <div
        class="absolute w-full h-full rotate-y-180 backface-hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 flex flex-col"
      >
        <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
          {{ food.name }}
        </h2>

        <div class="flex-1 overflow-y-auto">
          <div v-if="food.ingredients">
            <h3
              class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
            >
              Ingrédients:
            </h3>
            <p class="text-gray-700 dark:text-gray-300">
              {{ food.ingredients }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-4 mt-auto pt-4">
          <div class="flex gap-2">
            <UBadge v-if="food.is_halal" color="main" variant="subtle">
              Halal
            </UBadge>
            <UBadge v-if="food.is_vegan" color="main" variant="subtle">
              Vegan
            </UBadge>
          </div>

          <div class="flex gap-2">
            <UButton
              color="main"
              variant="outline"
              size="sm"
              class="flex-1"
              @click.stop="addToCart"
            >
              Ajouter au panier
            </UButton>
            <UButton
              v-if="canCustomize"
              color="main"
              variant="solid"
              size="sm"
              class="flex-1"
              @click.stop="isCustomizeOpen = true"
            >
              Personnaliser
            </UButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Customize Drawer - Teleported to avoid hydration issues -->
    <Teleport to="body">
      <MealCustomizeDrawer
        v-model="isCustomizeOpen"
        :meal="food"
        @meal-customized="handleMealCustomized"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import type { Repas, Ingredient } from "~/models/repas";

const props = defineProps<{
  food: Repas;
}>();

const isFlipped = ref(false);
const isCustomizeOpen = ref(false);
const cartStore = useCartStore();

const canCustomize = computed(() => {
  if (props.food.content && props.food.content.length > 0) return true;
  if (props.food.ingredients && props.food.ingredients.trim().length > 0)
    return true;
  return false;
});

// Methods
const addToCart = () => {
  cartStore.addRegularMeal(props.food);
  // Show success feedback (you can add a toast notification here)
};

const handleMealCustomized = (data: {
  meal: Repas;
  extras: Array<{
    ingredient: Ingredient;
    quantity: number;
  }>;
  menuContext?: {
    menuId: number;
    menuName: string;
  };
}) => {
  // Cette fonction est appelée quand un repas est personnalisé
  // Le drawer gère déjà l'ajout au panier, mais on peut ajouter une logique supplémentaire ici

  // Si c'est dans un contexte de menu, on peut gérer différemment
  if (data.menuContext) {
    // Ici on pourrait ajouter une logique spécifique pour les menus
  }
};
</script>

<style scoped>
.perspective {
  perspective: 1000px;
}

.preserve-3d {
  transform-style: preserve-3d;
}

.backface-hidden {
  backface-visibility: hidden;
}

.rotate-y-180 {
  transform: rotateY(180deg);
}

.flip-card-inner {
  transform-style: preserve-3d;
}
</style>
